/**
 * pages/Terminal.tsx — React port of app/layout.py.
 *
 * Structural 1:1 with the Python:
 *   app-wrap
 *     tape-bar          (TickerTape)
 *     main-grid
 *       left-pane       (Watchlist + Movers)
 *       resize-h        (handle-lc)
 *       center-pane
 *         chart-pane    (chart header + indicator selectors + chart)
 *         resize-v      (handle-cv)
 *         bottom-pane   (tab bar + active tab content)
 *       resize-h        (handle-cr)
 *       right-pane
 *         pane-hdr      ORDER TICKET (collapsible)
 *         OrderTicket
 *         pane-hdr      AI ANALYST
 *         quick-btns
 *         AIAnalyst chat + input
 */
import { useState } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { TickerTape }   from '@/components/TickerTape';
import { Watchlist }    from '@/components/Watchlist';
import { Movers }       from '@/components/Movers';
import { ChartHeader }  from '@/components/ChartHeader';
import { IndicatorRow } from '@/components/IndicatorRow';
import { MainChart }    from '@/components/MainChart';
import { BottomTabs }   from '@/components/BottomTabs';
import { OrderTicket }  from '@/components/OrderTicket';
import { AIAnalyst }    from '@/components/AIAnalyst';
import { useResizeHandles } from '@/hooks/useResizeHandles';
import { ThemePicker } from '@/components/ThemePicker';

export function Terminal() {
  const [orderOpen, setOrderOpen] = useState(true);
  // NOTE: the live tick WebSocket is now mounted in App's ProductBar (so it
  // stays connected across routes and the LIVE pip lives in the nav flex).
  // The hook writes to the global store, so Watchlist/Tape/Movers still get
  // live updates here without a second subscription.
  // Drag-to-resize the three pane dividers; persists to localStorage.
  useResizeHandles();

  return (
    <div className="app-wrap">
      {/* ── Tape bar ─────────────────────────────────────────────────── */}
      {/* The LIVE pip + latency moved OUT of this overlay and into the
          ProductBar flex (App.tsx) so it's laid out with the nav pills and
          can never be clipped by the tape edge or the fixed nav bar. */}
      <TickerTape />

      {/* ThemePicker relocated — floats below the ProductBar so users
          can access theme controls without cluttering the tape-bar zone
          alongside the LIVE pip. Positioned to align with the ProductBar
          right edge; sits in its own thin container below y:36 to clear
          the 34px tape-bar + a small gap. */}
      <div style={{
        position: 'fixed', top: 38, right: 12, zIndex: 999,
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'rgba(10,13,10,0.85)', padding: '3px 6px', borderRadius: 6,
        backdropFilter: 'blur(8px)',
      }}>
        <ThemePicker />
      </div>

      <div className="main-grid">
        {/* ── LEFT: watchlist + movers ───────────────────────────────── */}
        {/* Each core panel gets its own boundary. ErrorBoundary renders its
            children directly (no wrapper DOM) until it catches, so the flex +
            resize layout is untouched in the normal case -- but a throw in any
            one panel now shows a contained fallback instead of unmounting the
            whole terminal (blotter, kill switch and all). */}
        <div className="left-pane">
          <ErrorBoundary label="Watchlist"><Watchlist /></ErrorBoundary>
          <ErrorBoundary label="Movers"><Movers /></ErrorBoundary>
        </div>

        {/* Left↔center resize handle (placeholder; activated in v2) */}
        <div id="handle-lc" className="resize-h" />

        {/* ── CENTER: chart cockpit + bottom tabs ────────────────────── */}
        <div className="center-pane">
          <div className="chart-pane">
            <ErrorBoundary label="Chart header"><ChartHeader /></ErrorBoundary>
            <ErrorBoundary label="Indicators"><IndicatorRow /></ErrorBoundary>
            <ErrorBoundary label="Chart"><MainChart /></ErrorBoundary>
          </div>
          <div id="handle-cv" className="resize-v" />
          <div className="bottom-pane">
            <ErrorBoundary label="Bottom tabs"><BottomTabs /></ErrorBoundary>
          </div>
        </div>

        {/* Center↔right resize handle */}
        <div id="handle-cr" className="resize-h" />

        {/* ── RIGHT: order ticket (collapsible) + AI analyst ────────── */}
        <div className="right-pane">
          {/* Order ticket header — clicking toggles collapse */}
          <div
            className="pane-hdr"
            onClick={() => setOrderOpen(o => !o)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <span>ORDER TICKET</span>
            <span style={{ color: 'var(--accent)' }}>{orderOpen ? '▾' : '▸'}</span>
          </div>
          {orderOpen && (
            <div style={{
              padding: '6px 8px 8px', borderBottom: '1px solid #222',
              maxHeight: '46vh', overflowY: 'auto',
            }}>
              {/* The order ticket is the last thing that should vanish on an
                  unrelated render error -- it holds PLACE ORDER and the bracket
                  controls. Contain its own failures here. */}
              <ErrorBoundary label="Order ticket"><OrderTicket /></ErrorBoundary>
            </div>
          )}

          {/* AI analyst. Header stays STABLE — the co-pilot routes across
              every configured provider, so naming one here would churn on
              each answer. Per-answer provenance lives in the message
              footer, where it belongs. */}
          <div className="pane-hdr"><span>AI ANALYST  (smart-routed)</span></div>
          <ErrorBoundary label="AI analyst"><AIAnalyst /></ErrorBoundary>
        </div>
      </div>
    </div>
  );
}
